import { motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../contexts/ThemeContext';
import { trackCTA } from '../../../utils/metaPixel';

interface FinalCTASimpleProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'optimize') => void;
  /** Scroll to hero analyzer instead of routing to signup */
  scrollToHero?: boolean;
}

function useShapeColors(theme: string) {
  if (theme === 'dark') {
    return {
      SHAPE:       'rgba(242,242,243,0.10)',
      SHAPE_SOFT:  'rgba(242,242,243,0.07)',
      SHAPE_LIGHT: 'rgba(242,242,243,0.05)',
    };
  }
  return {
    SHAPE:       'rgba(167, 139, 250, 0.35)',
    SHAPE_SOFT:  'rgba(196, 181, 253, 0.28)',
    SHAPE_LIGHT: 'rgba(221, 214, 254, 0.22)',
  };
}

/** Shared with blog CTA — matches landing final CTA decorative art */
export const AbstractShapes: React.FC = () => {
  const { theme } = useTheme();
  const { SHAPE, SHAPE_SOFT, SHAPE_LIGHT } = useShapeColors(theme);
  return (
  <div className="relative w-full min-h-[200px] sm:min-h-[240px] flex items-center justify-center md:justify-end pr-0 md:pr-8 overflow-hidden">
    {/* Large circle (upper left, partially cropped) – animated */}
    <motion.div
      className="absolute rounded-full pointer-events-none -left-[15%] -top-[10%] sm:-left-[12%] sm:-top-[8%]"
      style={{
        width: '55%',
        paddingBottom: '55%',
        background: SHAPE,
        filter: 'blur(1px)',
      }}
      animate={{
        scale: [1, 1.06, 1],
        opacity: [0.9, 1, 0.9],
      }}
      transition={{ duration: 5, repeat: Infinity, repeatType: 'reverse' }}
      aria-hidden
    />

    {/* Triangle – overlapping large circle, animated */}
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: '20%',
        top: '24%',
        width: '22%',
        height: '22%',
        background: SHAPE_SOFT,
        clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
        filter: 'blur(0.5px)',
      }}
      animate={{
        y: [0, -8, 0],
        rotate: [0, 5, 0],
        opacity: [0.85, 1, 0.85],
      }}
      transition={{ duration: 4.5, delay: 0.5, repeat: Infinity, repeatType: 'reverse' }}
      aria-hidden
    />

    {/* Smaller circle – animated */}
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        left: '28%',
        top: '52%',
        width: '18%',
        paddingBottom: '18%',
        background: SHAPE_LIGHT,
        filter: 'blur(0.5px)',
      }}
      animate={{
        y: [0, 12, 0],
        x: [0, -6, 0],
        scale: [1, 1.1, 1],
        opacity: [0.8, 1, 0.8],
      }}
      transition={{ duration: 5.5, delay: 1, repeat: Infinity, repeatType: 'reverse' }}
      aria-hidden
    />

    {/* Rounded square – animated */}
    <motion.div
      className="absolute rounded-[22%] pointer-events-none"
      style={{
        left: '48%',
        top: '22%',
        width: '20%',
        paddingBottom: '20%',
        background: SHAPE_SOFT,
        filter: 'blur(0.5px)',
      }}
      animate={{
        y: [0, -10, 0],
        rotate: [0, 12, 0],
        scale: [1, 1.05, 1],
        opacity: [0.85, 1, 0.85],
      }}
      transition={{ duration: 4.8, delay: 0.3, repeat: Infinity, repeatType: 'reverse' }}
      aria-hidden
    />

    {/* Hexagon – bottom left area, animated */}
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: '8%',
        top: '62%',
        width: '24%',
        height: '28%',
        background: SHAPE_LIGHT,
        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
        filter: 'blur(0.5px)',
      }}
      animate={{
        y: [0, 8, 0],
        x: [0, 5, 0],
        rotate: [0, -8, 0],
        opacity: [0.75, 1, 0.75],
      }}
      transition={{ duration: 5.2, delay: 0.8, repeat: Infinity, repeatType: 'reverse' }}
      aria-hidden
    />

    {/* Extra small circle – layered depth */}
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        left: '62%',
        top: '58%',
        width: '12%',
        paddingBottom: '12%',
        background: SHAPE,
        filter: 'blur(0.5px)',
      }}
      animate={{
        scale: [1, 1.12, 1],
        opacity: [0.8, 1, 0.8],
      }}
      transition={{ duration: 4.2, delay: 1.2, repeat: Infinity, repeatType: 'reverse' }}
      aria-hidden
    />
  </div>
  );
};

const FinalCTASimple: React.FC<FinalCTASimpleProps> = ({ onNavigate, scrollToHero = false }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const handlePrimaryCta = () => {
    if (scrollToHero) {
      trackCTA('final_cta_upload_resume');
      document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', '/#hero');
      return;
    }
    trackCTA('final_cta_optimize');
    onNavigate?.('optimize');
  };

  return (
    <section id="cta" className="py-6 md:py-10 px-2 sm:px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col md:flex-row w-full max-w-[1600px] mx-auto rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:h-[260px] lg:h-[300px]"
        style={{ background: 'var(--brand-cta-bg)' }}
      >
        {/* Illustration — left half, fills the panel edge-to-edge with curved left corners */}
        <div className="hidden md:block md:w-1/2 shrink-0 relative overflow-hidden rounded-tl-2xl rounded-bl-2xl lg:rounded-tl-3xl lg:rounded-bl-3xl">
          <img
            src="/images/cta-illustration.png"
            alt="Professionals collaborating in a modern office"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="lazy"
            decoding="async"
          />
        </div>
        {/* Mobile illustration */}
        <img
          src="/images/cta-illustration.png"
          alt="Professionals collaborating in a modern office"
          aria-hidden
          className="md:hidden w-full max-w-[260px] mx-auto max-h-[180px] object-contain object-center order-2"
          loading="lazy"
          decoding="async"
        />
        {/* Right half — copy */}
        <div className="flex-1 md:w-1/2 md:shrink-0 min-w-0 flex flex-col justify-center items-center md:items-end text-center md:text-right order-1 md:order-2 py-6 sm:py-8 md:py-0 px-5 sm:px-6 md:pl-6 md:pr-5 lg:pl-8 lg:pr-8">
          <div className="w-full max-w-sm md:max-w-[22rem] lg:max-w-[24rem] md:ml-auto flex flex-col md:items-end md:text-right">
            <h2 className="text-xl sm:text-2xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-white mb-2 md:leading-tight">
              {t('finalCta.headline')}
            </h2>
            <p className="text-sm sm:text-base md:text-sm lg:text-base text-white/90 mb-4 max-w-sm md:max-w-none">
              {t('finalCta.subline')}
            </p>
            <button
              type="button"
              onClick={handlePrimaryCta}
              className="w-full px-5 py-2.5 font-bold rounded-xl transition-colors shadow-xl text-sm sm:text-base border-2"
              style={{
                background: theme === 'dark' ? 'rgba(242,242,243,0.12)' : '#ffffff',
                color: theme === 'dark' ? 'var(--vscode-text-primary)' : '#7c3aed',
                borderColor: theme === 'dark' ? 'rgba(242,242,243,0.20)' : 'transparent',
              }}
            >
              {t('finalCta.button')}
            </button>
            <p className="text-xs text-white/70 mt-2.5 md:text-right w-full">
              {t('finalCta.disclaimer')}
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default FinalCTASimple;
